import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Settings2 } from "lucide-react";

const inputCls =
  "w-full h-11 bg-zinc-950 border border-zinc-800 px-3 text-white text-sm focus:outline-none focus:border-[color:var(--mecha-red)]";
const labelCls =
  "block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1";

function previewOrderId(prefix, nextNumber) {
  const p = (prefix || "").trim().toUpperCase();
  const n = Math.max(1, parseInt(nextNumber, 10) || 1);
  const num = String(n).padStart(4, "0");
  return p ? `${p}-${num}` : num;
}

export default function Settings() {
  const [form, setForm] = useState({
    order_prefix: "",
    order_next_number: "1",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const preview = useMemo(
    () => previewOrderId(form.order_prefix, form.order_next_number),
    [form.order_prefix, form.order_next_number]
  );

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/settings");
      setForm({
        order_prefix: data.order_prefix || "",
        order_next_number: String(data.order_next_number || 1),
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/api/settings", {
        order_prefix: form.order_prefix.trim().toUpperCase(),
        order_next_number: parseInt(form.order_next_number, 10) || 1,
      });
      setForm({
        order_prefix: data.order_prefix || "",
        order_next_number: String(data.order_next_number),
      });
      toast.success(`Next order will be ${data.next_order_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-zinc-500 font-mono text-xs tracking-[0.3em]">
        LOADING...
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-12">
      <div className="mb-8">
        <div className="font-mono text-xs tracking-[0.3em] text-[color:var(--mecha-red)]">
          // CONFIG
        </div>
        <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl mt-3 leading-none">
          SETTINGS
        </h1>
        <p className="mt-4 text-zinc-400 text-sm max-w-xl">
          Control how new order IDs are generated at checkout.
        </p>
      </div>

      <form
        onSubmit={save}
        className="card-brutal max-w-xl"
        data-testid="settings-form"
      >
        <div className="p-5 border-b border-zinc-800 flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-[color:var(--mecha-red)]" />
          <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
            ORDER ID
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>PREFIX (OPTIONAL)</label>
              <input
                data-testid="settings-order-prefix"
                value={form.order_prefix}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    order_prefix: e.target.value.toUpperCase(),
                  }))
                }
                maxLength={12}
                className={inputCls + " font-mono tracking-widest"}
                placeholder="e.g. SS"
              />
            </div>
            <div>
              <label className={labelCls}>NEXT NUMBER</label>
              <input
                data-testid="settings-order-next"
                type="number"
                min="1"
                required
                value={form.order_next_number}
                onChange={(e) =>
                  setForm((f) => ({ ...f, order_next_number: e.target.value }))
                }
                className={inputCls + " font-mono"}
              />
            </div>
          </div>

          <div className="border border-zinc-800 bg-zinc-950 p-4">
            <div className="font-mono text-[9px] tracking-[0.3em] text-zinc-500">
              NEXT ORDER ID PREVIEW
            </div>
            <div
              data-testid="settings-order-preview"
              className="font-display text-3xl mt-2 text-[color:var(--neon-cyan)]"
            >
              {preview}
            </div>
            <div className="mt-2 text-xs text-zinc-500 leading-relaxed">
              Leave prefix empty for numbers only (e.g. 0100). With a prefix you
              get SS-0100. Existing orders are not changed.
            </div>
          </div>

          <button
            data-testid="settings-save"
            type="submit"
            disabled={saving}
            className="btn-brutal w-full h-12 bg-[color:var(--mecha-red)] text-white text-xs font-mono tracking-widest uppercase flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Settings"}
          </button>
        </div>
      </form>
    </div>
  );
}
